import type { ReactionSummary } from "./types";

/**
 * Pure optimistic toggle of the viewing user's reaction for one emoji on a
 * message. Returns the next reaction-summary list:
 *   - already reacted  → decrement count, clear reactedByMe, drop at zero
 *   - not yet reacted  → increment existing summary or append a new one
 *
 * The store applies this for instant feedback; the realtime reaction channel
 * then reloads the canonical counts, so this only needs to be directionally
 * correct, not authoritative.
 */
export function applyReactionToggle(
  reactions: ReactionSummary[],
  emoji: string
): ReactionSummary[] {
  const existing = reactions.find((r) => r.emoji === emoji);

  if (existing?.reactedByMe) {
    // Remove my reaction.
    const nextCount = existing.count - 1;
    if (nextCount <= 0) {
      return reactions.filter((r) => r.emoji !== emoji);
    }
    return reactions.map((r) =>
      r.emoji === emoji ? { ...r, count: nextCount, reactedByMe: false } : r
    );
  }

  if (existing) {
    // Add my reaction to an emoji others have used.
    return reactions.map((r) =>
      r.emoji === emoji ? { ...r, count: r.count + 1, reactedByMe: true } : r
    );
  }

  // First reaction of this emoji on the message.
  return [...reactions, { emoji, count: 1, reactedByMe: true }];
}
